import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useGetBenefitComparisonChecksQuery } from "store/queries/benefitComparison";
import {
  IComparisonCheck,
  IComparisonChecksRequest,
} from "types/BenefitComparison/BenefitComparison";

import {
  CHECK_STATUS_LABELS,
  CHECK_STATUS_SEVERITY,
  confidenceBand,
  formatComparisonValue,
  NEEDS_A_VERDICT,
} from "./benefitComparisonWording";
import { BenefitVerdictPanel } from "./BenefitVerdictPanel";
import { PverifyRequestLink, RemitClaimModal } from "./RemitAccuracyLinks";

const PAGE_SIZE = 50;

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-color-secondary">{children}</span>;
}

const requestBody = (row: IComparisonCheck) => (
  <PverifyRequestLink requestId={row.pverifyRequestId} />
);

const orderBody = (row: IComparisonCheck) =>
  row.orderId ? (
    <Link
      to={`/orders/${row.orderId}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      Estimate
    </Link>
  ) : (
    <span className="text-color-secondary">—</span>
  );

/** A check with neither a remit nor a human verdict: the only rows that carry a Review button. */
export const needsAVerdict = (row: IComparisonCheck) =>
  row.remit.value === null && row.verdict === null;

/**
 * CLE-2820 — the checks in one cluster: legacy, engine and AI side by side, then the two sources
 * that can say the engine was WRONG (the remit and the human verdict), then the check's Status.
 *
 * 🚨 CLICKING A ROW SELECTS AND EXPANDS IT; CLICKING THE OPEN ROW SHUTS IT AGAIN. The response panel
 * above loads that check and the verdict form opens INSIDE the row — under the table it ended up a
 * page away from the row it belonged to. One row is open at a time, shutting one leaves it selected
 * so the panel above keeps its check, and there are NO KEYBOARD SHORTCUTS — no digit keys, no J/K,
 * no global keydown handler.
 *
 * 🚨 THE REMIT VALUE IS A LINK to its claim. This is the one place somebody can CHECK a remit rather
 * than trust it, which matters because a remit outranks a human verdict in the scoring.
 */
export function BenefitComparisonChecks({
  request,
  selected,
  onSelect,
  showAccount = false,
}: {
  request: IComparisonChecksRequest;
  selected: string | null;
  onSelect: (pverifyResponseId: string) => void;
  showAccount?: boolean;
}) {
  const [offset, setOffset] = useState(0);
  const [claimId, setClaimId] = useState<string | null>(null);
  /**
   * 🚨 EXPANSION IS ITS OWN STATE, NOT A MIRROR OF THE SELECTION. Clicking the open row closes it —
   * which is the only way to read the table with nothing in the way — and closing it must NOT empty
   * the response panel above, so the row stays selected while its form is shut.
   */
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const requestKey = JSON.stringify(request);
  useEffect(() => setOffset(0), [requestKey]);
  const { data, isFetching } = useGetBenefitComparisonChecksQuery({
    ...request,
    limit: PAGE_SIZE,
    offset,
  });
  const page = data?.data;
  const rows = useMemo(() => page?.rows ?? [], [page]);

  // 🚨 BEFORE the effect below, not after: both run on mount, and the other way round this one shut
  // the row that one had just opened. A new cluster, or another page of one, starts closed rather
  // than reopening whatever now sits in the same position.
  useEffect(() => setExpandedId(null), [requestKey, offset]);
  // The first row is selected AND open on arrival, so the response panel above is never empty.
  useEffect(() => {
    if (!selected && rows.length > 0) {
      onSelect(rows[0].pverifyResponseId);
      setExpandedId(rows[0].pverifyResponseId);
    }
  }, [rows, selected, onSelect]);

  const current =
    rows.find((row) => row.pverifyResponseId === selected) ?? rows[0] ?? null;

  /** Click the closed row to open it (and select it); click the open one to shut it. */
  const toggleExpanded = (pverifyResponseId: string) => {
    setExpandedId((open) =>
      open === pverifyResponseId ? null : pverifyResponseId
    );
    onSelect(pverifyResponseId);
  };

  /** Review always OPENS, never toggles: it is a request to review, not a switch. */
  const openForReview = (pverifyResponseId: string) => {
    setExpandedId(pverifyResponseId);
    onSelect(pverifyResponseId);
  };
  /** 🚨 "N/A", not "no value": in a grid of figures a phrase reads as a value of its own. */
  const value = (amount: number | null) =>
    amount === null ? (
      <span className="text-color-secondary">N/A</span>
    ) : (
      formatComparisonValue(amount, request.benefit)
    );

  /**
   * 🚨 THE CONFIDENCE IS A WORD IN BRACKETS, not a decimal after a dot. A reader acts on "is this
   * worth checking", and 0.57 asks them to hold the whole distribution in their head to answer that;
   * the bands are cut where the measured match rate against remits actually breaks.
   */
  const aiBody = (row: IComparisonCheck) => {
    if (row.aiValue === null && row.aiConfidence === null)
      return <span className="text-color-secondary">not asked</span>;
    const band = confidenceBand(row.aiConfidence);
    return (
      <span className="white-space-nowrap">
        {value(row.aiValue)}
        {band && <Muted> ({band})</Muted>}
      </span>
    );
  };

  /** 🚨 A link, not a figure: the one place a remit can be checked rather than trusted. */
  const remitBody = (row: IComparisonCheck) => {
    if (row.remit.value === null)
      return <span className="text-color-secondary">—</span>;
    if (!row.remit.claimId) return <span>{value(row.remit.value)}</span>;
    return (
      <Button
        type="button"
        className="p-button-link p-0 font-semibold"
        label={String(formatComparisonValue(row.remit.value, request.benefit))}
        data-testid="benefitComparison.checks.remitLink"
        onClick={(event) => {
          event.stopPropagation();
          setClaimId(row.remit.claimId);
        }}
      />
    );
  };

  const verdictBody = (row: IComparisonCheck) => {
    if (!row.verdict) return <span className="text-color-secondary">—</span>;
    return (
      <span className="white-space-nowrap">
        {row.verdict.verdict === "VALUE"
          ? value(row.verdict.value)
          : "Can’t tell"}{" "}
        <span className="text-color-secondary">
          {row.verdict.reviewedByName ?? ""}
        </span>
      </span>
    );
  };

  const statusBody = (row: IComparisonCheck) => {
    if (!row.verification)
      return (
        <Tag
          value={NEEDS_A_VERDICT}
          data-testid="benefitComparison.checks.needsVerdict"
        />
      );
    return (
      <span className="flex flex-wrap gap-1">
        <Tag
          severity={CHECK_STATUS_SEVERITY[row.verification.status]}
          value={CHECK_STATUS_LABELS[row.verification.status]}
        />
        {/* 🚨 A remit and a verdict disagree. Flagged, both kept, never resolved. */}
        {row.remitConflict && (
          <Tag
            severity="danger"
            value="Conflict"
            data-testid="benefitComparison.checks.remitConflict"
          />
        )}
      </span>
    );
  };

  /** 🚨 The only entry point to reviewing in the whole design, and only where nothing confirms. */
  const reviewBody = (row: IComparisonCheck) =>
    needsAVerdict(row) ? (
      <Button
        type="button"
        label="Review"
        className="p-button-outlined p-button-sm"
        data-testid="benefitComparison.checks.review"
        onClick={(event) => {
          event.stopPropagation();
          openForReview(row.pverifyResponseId);
        }}
      />
    ) : null;

  /**
   * 🚨 THE VERDICT FORM LIVES INSIDE THE ROW. It used to sit under the table, which put it a whole
   * page below the row it belonged to once a cluster ran to fifty checks — you clicked a row near
   * the top and the form appeared off-screen. Expansion mirrors selection: one row open at a time,
   * the same row whose response is loaded in the panel above.
   */
  const rowExpansion = (row: IComparisonCheck) => (
    <div className="p-3" data-testid="benefitComparison.checks.panel">
      <div className="flex flex-wrap align-items-center gap-3 mb-3 text-sm">
        <span>{row.checkedAt.slice(0, 10)}</span>
        <PverifyRequestLink requestId={row.pverifyRequestId} />
        <span>
          Engine <strong>{value(row.engineValue)}</strong>
        </span>
        <span>
          Legacy <strong>{value(row.legacyValue)}</strong>
        </span>
        <span>
          AI <strong>{value(row.aiValue)}</strong>
          {confidenceBand(row.aiConfidence) && (
            <Muted> ({confidenceBand(row.aiConfidence)})</Muted>
          )}
        </span>
        {/* 🚨 The two sources that can say the engine was wrong, beside the question. */}
        <span
          className={row.remit.value === null ? "text-color-secondary" : ""}
        >
          Remit{" "}
          {row.remit.value === null ? (
            "N/A"
          ) : (
            <strong>{value(row.remit.value)}</strong>
          )}
        </span>
        <span className={row.verdict === null ? "text-color-secondary" : ""}>
          Human input{" "}
          {row.verdict === null ? (
            "N/A"
          ) : (
            <strong>
              {row.verdict.verdict === "VALUE"
                ? value(row.verdict.value)
                : "Can’t tell"}
            </strong>
          )}
        </span>
      </div>
      <BenefitVerdictPanel
        pverifyResponseId={row.pverifyResponseId}
        benefit={row.benefit}
        options={row.verdictOptions}
        verdict={row.verdict}
        remitValue={row.remit.value}
        remitConflict={row.remitConflict}
        agreement={row.verdictAgreement}
      />
    </div>
  );

  return (
    <>
      <DataTable
        value={rows}
        dataKey="pverifyResponseId"
        selectionMode="single"
        selection={current}
        onRowClick={(event) =>
          toggleExpanded((event.data as IComparisonCheck).pverifyResponseId)
        }
        expandedRows={expandedId ? { [expandedId]: true } : {}}
        rowExpansionTemplate={rowExpansion}
        onRowToggle={(event) => {
          // The expander chevron. One row open at a time, and re-clicking the open one shuts it.
          const opened = Object.keys(event.data ?? {}).find(
            (key) => key !== expandedId
          );
          if (opened) toggleExpanded(opened);
          else setExpandedId(null);
        }}
        lazy
        paginator
        rows={PAGE_SIZE}
        first={offset}
        totalRecords={page?.total ?? 0}
        onPage={(event) => setOffset(event.first)}
        loading={isFetching}
        emptyMessage="No checks."
        data-testid="benefitComparison.checks"
      >
        <Column expander style={{ width: "3rem" }} />
        <Column
          header="Checked"
          body={(row: IComparisonCheck) => row.checkedAt.slice(0, 10)}
        />
        {showAccount && <Column field="accountName" header="Account" />}
        <Column header="pVerify Request" body={requestBody} />
        <Column header="Estimate" body={orderBody} />
        <Column
          header="Engine"
          body={(row: IComparisonCheck) => value(row.engineValue)}
        />
        <Column
          header="Legacy"
          body={(row: IComparisonCheck) => value(row.legacyValue)}
        />
        <Column header="AI" body={aiBody} />
        <Column header="Remit" body={remitBody} />
        <Column header="Human input" body={verdictBody} />
        <Column header="Status" body={statusBody} />
        <Column header="" body={reviewBody} />
      </DataTable>

      {/* 🚨 The remit, opened rather than trusted — the same modal the Remit accuracy tab uses. */}
      {claimId && current && (
        <RemitClaimModal
          claimId={claimId}
          accountId={current.accountId}
          onHide={() => setClaimId(null)}
        />
      )}
    </>
  );
}
